// Mode card (ui-qol §4.2): "Pick a game mode" and four tiles (23 % wide × 110 px, #E2E2E2,
// radius 5) — Free-for-all #FF3F58, Battle Royale #00C1F1, Teams #FFAA00, Experimental #1E67E0.
// Hover and selected fill the tile in its colour with a white icon and label; modes the server
// has not enabled are grey at opacity 0.5 with a "soon" label and are not clickable. While in a
// money arena the tiles are locked (leave the money arena first).
//
// Consumer: ui/menu/layout.ts (app.ts passes `selectMode`).

import { type ModeId, PUBLIC_MODE_TILES } from '@ballio/shared';
import type { UiClickSource } from '../../events/bus.js';
import { h } from '../dom.js';
import { type I18nKey, t } from '../i18n/t.js';
import { modeIcon } from '../icons.js';

export interface ModeTileView {
  readonly mode: ModeId;
  readonly label: string;
  readonly enabled: boolean;
  readonly selected: boolean;
}

/** The four tiles (DOM-free). Only modes in `available` are enabled; `locked` disables all. */
export function modeTiles(
  available: ReadonlySet<ModeId>,
  selected: ModeId,
  locked = false,
): ModeTileView[] {
  return (PUBLIC_MODE_TILES as readonly ModeId[]).map((mode) => ({
    mode,
    label: t(`modes.${mode}` as I18nKey),
    enabled: !locked && available.has(mode),
    selected: mode === selected,
  }));
}

export interface ModesDeps {
  onSelect(mode: ModeId): void;
  playClick?(source: UiClickSource): void;
}

export interface ModesCard {
  readonly el: HTMLElement;
  set(available: ReadonlySet<ModeId>, selected: ModeId, locked: boolean): void;
}

export function createModesCard(deps: ModesDeps): ModesCard {
  const tiles = new Map<ModeId, HTMLButtonElement>();
  const row = h('div', { class: 'mode-tiles', role: 'radiogroup', 'aria-label': t('modes.title') });
  for (const mode of PUBLIC_MODE_TILES as readonly ModeId[]) {
    const btn = h(
      'button',
      {
        type: 'button',
        class: `mode-tile mode-${mode}`,
        role: 'radio',
        'aria-checked': 'false',
        onClick: () => {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          deps.playClick?.('mode');
          deps.onSelect(mode);
        },
      },
      h('span', { class: 'mode-icon' }, modeIcon(mode)),
      h('span', { class: 'mode-label' }, t(`modes.${mode}` as I18nKey)),
      h('span', { class: 'mode-soon' }, t('modes.soon')),
    );
    tiles.set(mode, btn);
    row.appendChild(btn);
  }
  const el = h(
    'section',
    { class: 'card modes-card' },
    h('h2', { class: 'card-title' }, t('modes.title')),
    row,
  );
  return {
    el,
    set(available, selected, locked): void {
      for (const v of modeTiles(available, selected, locked)) {
        const btn = tiles.get(v.mode);
        if (!btn) continue;
        btn.setAttribute('aria-disabled', v.enabled ? 'false' : 'true');
        btn.setAttribute('aria-checked', v.selected ? 'true' : 'false');
        btn.classList.toggle('is-selected', v.selected);
        btn.classList.toggle('is-disabled', !v.enabled && !v.selected);
        btn.classList.toggle('is-soon', !available.has(v.mode));
      }
    },
  };
}
